import {
  EXAM_EVENT_EXAMINEE_STATUS_LABEL,
  useExamEventDetails,
} from '@exam-center/exam-event-management';
import { ExamType, FilterOperator, useSearchExamEventExamineesQuery } from '@exam-center/graphql';
import { useMemo, useState } from 'react';

import { type ExamineeColumn, ExamineesDataGrid } from '../../components';
import { ExamineeActionMenu } from '../examinee-action-menu';

interface ExamineeListProps {
  examEventId: string;
}

export default function ExamineeList(props: Readonly<ExamineeListProps>) {
  const { examEventId } = props;

  const {
    examEvent: { examType },
  } = useExamEventDetails();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const { data: searchExamineesResult, loading: searchingForExaminees } =
    useSearchExamEventExamineesQuery({
      variables: {
        searchExamEventExaminees: {
          filter: {
            conditions: [
              {
                field: 'examEventId',
                operator: FilterOperator.Equal,
                value: examEventId,
              },
            ],
          },
          paging: {
            pageSize,
            currentPage: page,
          },
        },
      },
    });

  const examEventExamineesResults = searchExamineesResult?.ecSearchExamEventExaminees;
  const totalExaminees = examEventExamineesResults?.total ?? 0;

  const examinees: ExamineeColumn[] = useMemo(() => {
    if (!examEventExamineesResults) return [];

    const { examEventExaminees } = examEventExamineesResults;

    return examEventExaminees.map((examEventExaminee) => ({
      id: examEventExaminee.id,
      title: examEventExaminee.examinee?.title,
      firstName: examEventExaminee.examinee?.firstName,
      middleName: examEventExaminee.examinee?.middleName,
      lastName: examEventExaminee.examinee?.lastName,
      email: examEventExaminee.email,
      examCourseCode: examEventExaminee.examCourse?.code,
      isExamReady: examEventExaminee.isExamReady,
      eKycStatus: examEventExaminee.examinee.eKycStatus,
      status: EXAM_EVENT_EXAMINEE_STATUS_LABEL[examEventExaminee.status],
      remark: examEventExaminee.remark,
      cancelationReasonCategory: examEventExaminee.cancelationReason,
      action: (
        <ExamineeActionMenu
          examEventExamineeId={examEventExaminee.id}
          examEventExamineeStatus={examEventExaminee.status}
          examCourseCode={examEventExaminee.examCourse?.code ?? ''}
          examEventExamineeRegistrationStatus={examEventExaminee.registrationStatus}
          examEventSessionExamineesExamStartedAt={
            examEventExaminee.examEventSessionExaminees?.map((s) => s.examStartedAt) ?? []
          }
        />
      ),
    }));
  }, [examEventExamineesResults]);

  return (
    <ExamineesDataGrid
      rows={examinees}
      variant={examType === ExamType.Computer ? 'computer' : 'paper'}
      loading={searchingForExaminees}
      slotProps={{
        footer: {
          total: totalExaminees,
          pagination: {
            page,
            count: totalExaminees,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              setPage(page);
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              setPage(0);
              return setPageSize(pageSize);
            },
          },
        },
      }}
    />
  );
}
